import React, {useContext} from 'react'
import clsx from 'clsx'
import {KTIcon, ID} from '@/_metronic/helpers'
import {CustomerContext} from '../CustomerContext'
import {ReactCountryFlag} from 'react-country-flag'
import {RouteTabs} from '@/components/tabs/RouteTabs'
import {useIntl} from 'react-intl'
import { intervalToDuration } from 'date-fns';

import { impersonate } from '../../core/_requests';

interface Props {
  customerId: ID;
}

export const ImpersonateButton = ({ customerId }: Props) => {
  const handleClick = () => {
    impersonate(customerId);
  };

  return (
    <button type="button" onClick={handleClick} className="btn btn-light-danger ms-2">
      Se connecter
    </button>
  );
};


/**
 * Header
 */
const Header = () => {
  const { customer, indicators } = useContext(CustomerContext)
  const intl = useIntl()

  if (!customer) {
    return (<></>)
  }

  const since = intervalToDuration({
    start: customer.createdAt,
    end: new Date()
  })

  const status = customer.statuses.at(-1)
  const tabs = [{
    label: 'Overview',
    route: `/p2c/customers/${customer._id}/overview`
  }, {
    label: 'Reports',
    route: `/p2c/customers/${customer._id}/reports`
  }, {
    label: 'Statement',
    route: `/p2c/customers/${customer._id}/statements`
  }, {
    label: 'Settings',
    route: `/p2c/customers/${customer._id}/settings`
  }, {
    label: 'Connections',
    route: `/p2c/customers/${customer._id}/auth`
  }, {
    label: 'Logs',
    route: `/p2c/customers/${customer._id}/logs`
  }]

  if (customer?.sponsorship?.statuses?.at(-1)?.status === 'APPROVED') {
    tabs.push({
      label: 'Sponsorship',
      route: `/p2c/customers/${customer._id}/sponsorship`
    })
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-body pt-9 pb-0'>
        <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
          <div className='me-7 mb-4'>
            <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative'>
              <img
                src={`https://www.gravatar.com/avatar/${customer.avatar.hash}?d=${customer.avatar.type}`}
                alt={customer.username}
                className='w-100'
              />
            </div>
          </div>

          <div className='flex-grow-1'>
            <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
              <div className='d-flex flex-column'>
                <div className='d-flex align-items-center mb-2'>
                  <span className='text-gray-800 fs-2 fw-bolder me-1'>
                    {customer.username}
                  </span>
                </div>

                <div className='d-flex flex-wrap fw-bold fs-6 mb-4 pe-2'>
                  <span className='d-flex align-items-center text-gray-400 mb-2 me-5' title="Status">
                    <KTIcon
                      iconName='status'
                      className={clsx("fs-1 text-primary pe-2", {
                        'text-primary': status?.status === 'PENDING_VALIDATION',
                        'text-success': status?.status === 'VALIDATED',
                        'text-warning': status?.status === 'DELETED',
                        'text-danger': status?.status === 'LOCKED'
                      })}
                    />
                    {intl.formatMessage({id: `CUSTOMER_STATUS_${status?.status}`})}
                  </span>
                  <span className='d-flex align-items-center text-gray-400 mb-2 me-5'>
                    <KTIcon iconName='sms' className='fs-4 me-1 text-primary' />
                    {customer.email}
                  </span>

                  <span className='d-flex align-items-center text-gray-400 mb-2'>
                    <KTIcon
                      iconName={customer.settings.otpEnabled ? 'shield-tick' : 'shield-slash'}
                      className={clsx('fs-4 me-1', {
                        'text-success': customer.settings.otpEnabled,
                        'text-danger': !customer.settings.otpEnabled
                      })}
                    />
                    2FA
                  </span>
                  <ImpersonateButton customerId={customer._id} />
                </div>
              </div>

              <div className='d-flex align-items-center w-200px w-sm-300px flex-column mt-3'>
                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <span className='fw-bold fs-6 text-gray-400'>
                    Join since:
                  </span>
                  <span className='fw-bolder'>
                    {intl.formatDate(customer.createdAt, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <span className='fw-bold fs-6 text-gray-400'>
                    Last Seen:
                  </span>
                  <span className='fw-bolder'>
                    {customer.lastSeen?.date ? intl.formatDate(customer.lastSeen.date, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      second: 'numeric'
                    }) : '-'}
                  </span>
                </div>

                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <span className='fw-bold fs-6 text-gray-400'>
                    Member since:
                  </span>
                  <span className='fw-bolder'>
                    {
                      Object.entries(since)
                        .filter(([key, value]) => ['years', 'months', 'days'].includes(key) && value && value > 0)
                        .map(([key, value]) => `${value} ${key.replace(/s$/, '')}${value > 1 ? 's' : ''}`)
                        .join(', ')
                    }
                  </span>
                </div>
              </div>
            </div>

            <div className='d-flex flex-wrap flex-stack'>
              <div className='d-flex flex-column flex-grow-1 pe-8'>
                <div className='d-flex flex-wrap'>
                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <ReactCountryFlag
                        countryCode={customer.country ?? ''}
                        style={{ fontSize: '1.5em' }}
                        className='h-20px rounded-1 ms-2 pe-2'
                      />
                      <div className="fs-2 fw-bold" >{customer.country}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Country</div>
                  </div>

                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="enjin-coin" className="fs-1 pe-2 text-primary"/>
                      <div className="fs-2 fw-bold" >
                        {intl.formatNumber(indicators.credit - indicators.debit)}
                      </div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Balance</div>
                  </div>

                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="enjin-coin" className="fs-1 pe-2 text-success"/>
                      <div className="fs-2 fw-bold" >{intl.formatNumber(indicators.credit)}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Credits</div>
                  </div>


                  <div className="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3">
                    <div className="d-flex align-items-center">
                      <KTIcon iconName="enjin-coin" className="fs-1 pe-2 text-danger"/>
                      <div className="fs-2 fw-bold" >{intl.formatNumber(indicators.debit)}</div>
                    </div>
                    <div className="fw-semibold fs-6 text-gray-500">Debit</div>
                  </div>

                </div>

              </div>

              <div className='d-flex align-items-center w-200px w-sm-300px flex-column mt-3'>
                <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                  <span className='fw-bold fs-6 text-gray-400'>Customer Completion</span>
                  <span className='fw-bolder fs-6'>50%</span>
                </div>
                <div className='h-5px mx-3 w-100 bg-light mb-3'>
                  <div
                    className='bg-success rounded h-5px'
                    role='progressbar'
                    style={{width: '50%'}}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RouteTabs tabs={tabs} />
      </div>
    </div>
  )
}

export default Header
